import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  CheckCircle2,
  FileText,
  FolderOpen,
  Globe,
  MapPin,
  Megaphone,
  Briefcase,
  MessageSquare,
} from 'lucide-react'
import { usePublicProfile } from '@/hooks'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'

const STAT_CONFIG: Array<{
  key: keyof import('@/lib/api').PublicProfileStatistics
  label: string
  icon: React.ComponentType<{ className?: string }>
}> = [
  { key: 'projects_posted', label: 'Projects posted', icon: Briefcase },
  { key: 'open_projects', label: 'Open projects', icon: FolderOpen },
  { key: 'public_projects', label: 'Public projects', icon: Megaphone },
  { key: 'proposals_submitted', label: 'Proposals submitted', icon: FileText },
  {
    key: 'proposals_accepted',
    label: 'Proposals accepted',
    icon: CheckCircle2,
  },
]

function formatDate(value: string | null | undefined) {
  if (!value) return null
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      year: 'numeric',
    }).format(new Date(value))
  } catch (error) {
    return null
  }
}

function formatCurrency(value: string | number | null | undefined) {
  if (value === null || value === undefined) return null
  const numeric = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(numeric)) return null
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numeric)
}

function statusVariant(status: string) {
  switch (status) {
    case 'open':
      return 'secondary'
    case 'in_progress':
      return 'default'
    case 'completed':
      return 'outline'
    case 'cancelled':
      return 'destructive'
    default:
      return 'secondary'
  }
}

function capitalize(value: string) {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function PublicProfile() {
  const { username } = useParams<{ username: string }>()
  const profileQuery = usePublicProfile(username)

  if (profileQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-5xl space-y-6 px-4 py-12">
          <Skeleton className="h-16 w-1/2" />
          <Skeleton className="h-48 w-full" />
          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (profileQuery.error || !profileQuery.data) {
    const notFound = (profileQuery.error as any)?.response?.status === 404
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-3xl px-4 py-20">
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <BadgeCheck className="h-10 w-10 text-destructive" />
              <h2 className="text-2xl font-semibold text-foreground">
                {notFound ? 'Profile not found' : 'Unable to load profile'}
              </h2>
              <p className="max-w-md text-sm text-muted-foreground">
                {notFound
                  ? 'We could not find a profile for the requested user. Please check the link and try again.'
                  : 'Something went wrong while loading this profile. Please refresh the page or try later.'}
              </p>
              <Button asChild variant="outline">
                <Link to="/developers">Back to directory</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const profile = profileQuery.data
  const displayName = profile.full_name || profile.user.username
  const memberSince = formatDate(profile.member_since)
  const hourlyRate = formatCurrency(profile.hourly_rate ?? null)
  const isDeveloper = profile.role === 'developer'

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b bg-gradient-to-br from-background via-background to-muted/40">
        <div className="bg-grid-white/[0.02] absolute inset-0" />
        <div className="container relative mx-auto max-w-5xl px-4 py-12">
          <div className="flex flex-wrap items-start gap-6">
            <Avatar className="h-20 w-20 border border-border/50">
              {profile.avatar_url ? (
                <AvatarImage src={profile.avatar_url} alt={displayName} />
              ) : (
                <AvatarFallback className="text-lg font-semibold uppercase">
                  {displayName.charAt(0)}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-foreground">{displayName}</h1>
                <Badge variant="secondary" className="capitalize">
                  {profile.role}
                </Badge>
                {profile.email_verified && (
                  <Badge variant="outline" className="gap-1 text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span>@{profile.user.username}</span>
                {profile.company && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {profile.company}
                  </span>
                )}
                {profile.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.location}
                  </span>
                )}
                {memberSince && (
                  <span className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    Member since {memberSince}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <Button asChild variant="ghost" className="gap-2 self-start">
                <Link to={isDeveloper ? '/developers' : '/projects'}>
                  <ArrowLeft className="h-4 w-4" />
                  {isDeveloper ? 'Back to developer directory' : 'Back to projects'}
                </Link>
              </Button>
              {hourlyRate && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-primary">
                  <p className="text-xs uppercase tracking-wide">Hourly rate</p>
                  <p className="text-lg font-semibold">{hourlyRate}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-5xl space-y-8 px-4 py-12">
        <div className="grid gap-8 lg:grid-cols-[2fr,1fr]">
          <section className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                {profile.bio ? <p>{profile.bio}</p> : <p>This user has not added a bio yet.</p>}
                {isDeveloper && (
                  <p>
                    Looking to collaborate with this developer? Explore their public engagements
                    below or invite them to your private project.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Skills & Focus</CardTitle>
              </CardHeader>
              <CardContent>
                {profile.skills && profile.skills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill) => (
                      <Badge key={skill.id} variant="outline" className="text-xs">
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No skills shared yet.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{isDeveloper ? 'Recent contributions' : 'Public projects'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.public_projects.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {isDeveloper
                      ? 'No public activity to showcase yet.'
                      : 'No public-facing projects available yet.'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {profile.public_projects.map((project) => (
                      <Card key={project.id} className="border-border/60">
                        <CardHeader className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <CardTitle className="text-lg font-semibold text-foreground">
                                {project.title}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground">
                                {project.account_name}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <Badge variant="outline" className="capitalize">
                                {project.relationship}
                              </Badge>
                              <Badge variant={statusVariant(project.status)} className="capitalize">
                                {capitalize(project.status)}
                              </Badge>
                            </div>
                          </div>
                          <p className="line-clamp-3 text-sm text-muted-foreground">
                            {project.description}
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-3 text-xs text-muted-foreground">
                          <div className="flex flex-wrap gap-3">
                            <span>
                              Posted{' '}
                              {new Intl.DateTimeFormat(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }).format(new Date(project.created_at))}
                            </span>
                            <span>
                              Updated{' '}
                              {new Intl.DateTimeFormat(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }).format(new Date(project.updated_at))}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {project.skills.map((skill) => (
                              <Badge key={`${project.id}-${skill.id}`} variant="outline">
                                {skill.name}
                              </Badge>
                            ))}
                          </div>
                          <div className="pt-2">
                            <Button asChild size="sm" variant="link" className="px-0 text-primary">
                              <Link to={`/projects/${project.id}`}>View project</Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <aside className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile snapshot</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  {STAT_CONFIG.map(({ key, label, icon: Icon }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{label}</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground">
                        {profile.statistics[key] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Connect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Start a conversation with {displayName.split(' ')[0]}</p>
                <Button asChild className="w-full gap-2">
                  <Link to={`/messages?user=${profile.user.username}`}>
                    <MessageSquare className="h-4 w-4" />
                    Send Message
                  </Link>
                </Button>
                {isDeveloper && (
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/post">Share a project brief</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  )
}
